/**
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * `GD-14` — LỜI NGƯỜI DÙNG: MỘT BẢNG DỊCH CHUNG cho nhật ký lệnh, nhật ký tủ gửi lên và
 * mọi chỗ khác phải đổi MÃ MÁY thành CÂU NGƯỜI ĐỌC.
 *
 * 🔴 LUẬT L-CHỮ-NGƯỜI-DÙNG: mặt trước chỉ có tiếng Việt có dấu, câu trọn nghĩa. Mã lệnh,
 *   JSON, thẻ log, `requestId`, `epochMs`, `tz` … chỉ nằm trong "Chi tiết kỹ thuật" (Quản trị).
 *
 * ⛔ KHÔNG BỊA. Mã chưa có lời dịch thì trả `{ cau: 'Sự kiện', ma }` — trang vẽ `ma` thành
 *   chip — và `console.warn` MỘT lần cho mỗi mã. Một câu đoán nghe hợp lý còn tệ hơn một chip
 *   mã: người trực tin câu đoán, còn chip thì họ biết là phải hỏi.
 *
 * 📜 NGUỒN CỦA TỪNG BẢNG (đọc từ mã, 29/09 — `web/scripts/kiem_loi_nguoi.ts` đọc lại chính
 *   những chỗ này mỗi lần chạy, thiếu câu nào thì đỏ đúng mã ấy):
 *     · `LOAI_LENH`       ← `server/src/proto/codec.ts` `MA_LENH` + `'RELEASE_V1'` (deviceGateway)
 *     · `MA_ACK`          ← `server/src/services/ack.ts` `TEN_KET_QUA_ACK`
 *     · `CAU_TU_TRA_LOI`  ← chuỗi `gui_ack(…)` / `LY_DO(…)` trong phần sụn `kapp_v2_app.cpp`
 *     · `CAU_MAY_CHU`     ← chuỗi `detail:` máy chủ tự ghi vào hàng `Command`
 *     · `MA_SU_KIEN`      ← thẻ `DeviceLog`: `ACK-*`, `EXC-*`, `ALARM-LA-*` (deviceGateway)
 *     · mã CẢNH BÁO       ← `BANG_MUC` của `canhbao.ts` (bảng phân mức có sẵn — KHÔNG chép lại);
 *                           `PopupCanhBao` gọi `phanMuc`/`cauChanPopup` QUA tệp này (GD-14 vòng 2)
 *
 * ⚠ Tệp này KHÔNG nhập React — bộ tự kiểm chạy nó bằng `tsx`, không có trình duyệt.
 * ═══════════════════════════════════════════════════════════════════════════════════════
 */
import {
  BANG_MUC, cauChanPopup, chuanHoaMa, phanMuc as phanMucGoc, type KetQuaPhanMuc,
} from './canhbao';

export { cauChanPopup };

/** Kết quả dịch: `ma` có mặt ⇔ CHƯA dịch được — trang vẽ nó thành chip cạnh `cau`. */
export interface LoiNguoi {
  cau: string;
  ma?: string;
}

/* ─────────────────────────────────────────────────── mã chưa dịch: báo một lần ── */

const daBao = new Set<string>();
/** Nhật ký cho người dịch: mỗi mã chưa có câu chỉ kêu MỘT lần mỗi lượt mở trang. */
export function baoChuaDich(ma: string): void {
  if (daBao.has(ma)) return;
  daBao.add(ma);
  // eslint-disable-next-line no-console
  console.warn(`[loi_nguoi] mã chưa có lời dịch: ${ma} — thêm vào web/src/lib/loi_nguoi.ts`);
}

const chuaDich = (ma: string): LoiNguoi => {
  baoChuaDich(ma);
  return { cau: 'Sự kiện', ma };
};

/* ─────────────────────────────────────────────────────────── 1 · LOẠI LỆNH ── */

type Tai = Record<string, unknown>;
const ngoHopLe = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;

/**
 * Câu cho cột NỘI DUNG. `moTaNgo` là câu `moTaTaiLenh(payload)` trang đã tính — dùng lại cho
 * `SET_ALL_OUTPUTS` ("ngõ 1: BẬT · ngõ 2: TẮT · ngõ 3: TẮT"), KHÔNG chép lại cách đọc mảng.
 * Hàm trả `null` = payload không đúng hình dạng của loại lệnh ⇒ dùng câu chung của loại.
 */
export const LOAI_LENH: Record<string, { chung: string; theoTai?: (p: Tai, moTaNgo: string) => string | null }> = {
  SET_OUTPUT: {
    chung: 'Bật/tắt một ngõ',
    theoTai: (p) => (ngoHopLe(p.output) && typeof p.on === 'boolean'
      ? `${p.on ? 'Bật' : 'Tắt'} ngõ ${p.output}` : null),
  },
  SET_ALL_OUTPUTS: {
    chung: 'Đặt cả ba ngõ',
    theoTai: (p, moTaNgo) => (Array.isArray(p.outputs) && p.outputs.length > 0
      && !/KHÔNG ĐỌC ĐƯỢC/.test(moTaNgo) ? `Đặt cả ba ngõ: ${giuNgo(moTaNgo)}` : null),
  },
  APPLY_SCHEDULE: {
    chung: 'Gửi lịch mới',
    // ⚠ Payload máy chủ lưu KHÔNG có số khe (`{revision, wireVer, ngoRa}`) ⇒ không viết
    //   "(N khe)" — ghi nợ NỢ-NK-01, không đoán.
    theoTai: (p) => (ngoHopLe(p.ngoRa) ? `Gửi lịch mới cho ngõ ${p.ngoRa}` : null),
  },
  RELEASE_CONTROL: { chung: 'Trả quyền về lịch' },
  RELEASE_V1: { chung: 'Trả quyền về lịch' },
  SYNC_RTC: { chung: 'Đồng bộ giờ cho tủ' },
  REQUEST_STATE: { chung: 'Hỏi trạng thái tủ' },
  SET_DIM: { chung: 'Đặt độ sáng đèn' },
  REBOOT: { chung: 'Khởi động lại tủ' },
  READ_METER: { chung: 'Đọc đồng hồ điện ngay' },
};

export function noiDungLenh(kind: string, payload: unknown, moTaNgo: string): LoiNguoi {
  const loai = LOAI_LENH[kind];
  if (!loai) return chuaDich(kind);
  const p = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Tai) : {};
  return { cau: loai.theoTai?.(p, moTaNgo) ?? loai.chung };
}

/* ────────────────────────────────────────────────────── 2 · KẾT QUẢ CỦA TỦ ── */

/** `AckResult` (kapp_v2.proto) — tên trên dây ⇒ câu. */
export const MA_ACK: Record<string, string> = {
  ACK_OK: 'Tủ đã nhận lệnh',
  ACK_REJECTED: 'tủ không nhận lệnh này',
  ACK_UNSUPPORTED: 'tủ chưa hỗ trợ lệnh này',
  ACK_BUSY: 'tủ đang bận',
  ACK_BAD_PARAM: 'thông số lệnh không hợp lệ',
  ACK_STORAGE_ERROR: 'tủ lỗi khi ghi bộ nhớ',
  ACK_EXPIRED: 'lệnh tới tủ đã quá hạn',
};

const BAT_TAT = (s: string) => (s === 'BAT' ? 'BẬT' : 'TẮT');
/**
 * "ngõ 3: TẮT" ⇒ "ngõ 3 TẮT" với dấu cách KHÔNG NGẮT: dòng chỉ được gãy ở " · ", không gãy giữa
 * một ngõ và trạng thái của nó (đo ảnh 1440 lượt 1: "ngõ 3:" / "TẮT" rơi hai dòng).
 */
export const giuNgo = (s: string) => s.replace(/ngõ (\d+):? (BẬT|TẮT)/g, 'ngõ\u00a0$1\u00a0$2');
const bo01 = (s: string) => giuNgo(s.split('').map((c, i) => `ngõ ${i + 1} ${c === '1' ? 'BẬT' : 'TẮT'}`).join(' · '));

/** Lý do trong một khe lịch (sau tiền tố `ngo N o M: `). */
const LY_DO_KHE: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^index ngoai 0\.\.(\d+)$/, (m) => `số thứ tự khe phải từ 0 đến ${m[1]}`],
  [/^index TRUNG$/, () => 'trùng số thứ tự khe'],
  [/^daysOfWeek co (\d+), phai dung (\d+)$/, (m) => `danh sách thứ có ${m[1]} mục, phải đúng ${m[2]}`],
  [/^enabled=true ma ca 7 thu deu false$/, () => 'khe đang bật nhưng không chọn thứ nào'],
  [/^gio bat\/tat thieu hoac ngoai 00:00\.\.23:59$/, () => 'giờ bật/tắt bị thiếu hoặc ngoài khoảng 00:00 đến 23:59'],
  [/^(\d+) ngay dac biet > toi da (\d+)$/, (m) => `${m[1]} ngày đặc biệt, quá mức tối đa ${m[2]}`],
  [/^ngay dac biet (\d+)\/(\d+)$/, (m) => `ngày đặc biệt ${m[1]}/${m[2]} không hợp lệ`],
];

/**
 * Chuỗi `detail` do PHẦN SỤN gửi (không dấu, giọng máy) ⇒ câu có dấu. Khớp NGUYÊN chuỗi
 * (`^…$`): chuỗi lạ thì rơi xuống "chưa dịch", không bị một mẫu lỏng nuốt nhầm.
 */
export const CAU_TU_TRA_LOI: [RegExp, (m: RegExpMatchArray) => string][] = [
  // — thành công —
  [/^ngo (\d+) -> (BAT|TAT)$/, (m) => `ngõ\u00a0${m[1]} đã ${BAT_TAT(m[2])}`],
  [/^dat ([01]+)$/, (m) => `đã đặt ${bo01(m[1])}`],
  [/^da tra quyen ve lich$/, () => 'đã trả quyền về lịch'],
  [/^dang gui ba khung$/, () => 'tủ đang gửi trạng thái lên'],
  [/^dang gui so do$/, () => 'tủ đang gửi số đo lên'],
  [/^se khoi dong lai sau ~(\d+) ms$/, (m) => `tủ sẽ khởi động lại sau khoảng ${(Number(m[1]) / 1000).toLocaleString('vi-VN')} giây`],
  [/^da ap$/, () => 'tủ đã áp lịch mới'],
  [/^epochMs=-?\d+ tz=-?\d+$/, () => 'tủ đã đặt giờ theo máy chủ'],
  // — từ chối —
  [/^TU CHOI epochMs=-?\d+ tz=-?\d+$/, () => 'giờ máy chủ gửi không hợp lệ'],
  [/^output=(\d+) ngoai 1\.\.(\d+)$/, (m) => `không có ngõ ${m[1]} (tủ chỉ có ngõ 1 đến ${m[2]})`],
  [/^lenh qua han \(ttlSec\)$/, () => 'lệnh tới tủ đã quá hạn'],
  [/^dong ho tu lech ~(-?\d+) phut - dong bo gio roi thu lai$/, (m) => `đồng hồ tủ lệch khoảng ${m[1]} phút — đồng bộ giờ rồi thử lại`],
  [/^outputStates co (\d+), can (\d+)$/, (m) => `lệnh có ${m[1]} ngõ, tủ cần đúng ${m[2]}`],
  [/^thieu truong schedule$/, () => 'gói lịch không có nội dung'],
  [/^xem log tu$/, () => 'tủ không nói lý do'],
  [/^lenh nay tu chua cai$/, () => 'tủ chưa hỗ trợ lệnh này'],
  [/^n_outputs=(\d+), hop dong la 1$/, (m) => `gói lịch có ${m[1]} ngõ, mỗi lần chỉ được một ngõ`],
  [/^ngo (\d+): mode=-?\d+, tu chi co TIMER\/SEASON$/, (m) => `ngõ ${m[1]}: chế độ không hỗ trợ (tủ chỉ có hẹn giờ và theo mùa)`],
  [/^ngo (\d+) o (\d+): (.+)$/, (m) => {
    const lyDo = LY_DO_KHE.map(([re, f]) => { const k = m[3].match(re); return k ? f(k) : null; }).find(Boolean);
    return lyDo ? `ngõ ${m[1]}, khe ${m[2]}: ${lyDo}` : '';   // '' = chưa dịch, xem dichTraLoi
  }],
  [/^ghi NVS xong nhung doc lai KHONG khop$/, () => 'tủ ghi bộ nhớ xong nhưng đọc lại không khớp'],
];

/** Chuỗi `detail` MÁY CHỦ tự ghi vào hàng `Command` (đã có dấu, nhưng lộ tên khung). */
export const CAU_MAY_CHU: Record<string, string> = {
  'Suy đoán từ StateOnChange kế tiếp': 'máy chủ suy ra từ trạng thái tủ báo lên ngay sau',
  'Đã phát lại bằng lệnh mới khi tủ online': 'đã gửi lại bằng lệnh mới khi tủ có kết nối',
  'Tủ offline khi ra lệnh; đã phát lại bằng lệnh mới': 'tủ mất kết nối lúc ra lệnh; đã gửi lại bằng lệnh mới',
  'Không thấy StateOnChange xác nhận': 'Không nhận được xác nhận của tủ trong thời gian chờ',
};

/** Dịch một chuỗi `detail` — trả `null` nếu chưa có câu (không đoán). */
export function dichTraLoi(detail: string): string | null {
  const t = detail.trim();
  if (t in CAU_MAY_CHU) return CAU_MAY_CHU[t];
  for (const [re, f] of CAU_TU_TRA_LOI) {
    const m = t.match(re);
    if (m) return f(m) || null;
  }
  return null;
}

/**
 * Câu đứng cạnh huy hiệu trong ô TRẠNG THÁI (gộp cột GHI CHÚ cũ). `null` = huy hiệu đã đủ nghĩa.
 * ⚠ "Tủ từ chối:" CHỈ khi hàng là FAILED và `detail` KHÔNG phải câu máy chủ tự ghi — máy chủ
 *   cũng đặt FAILED cho ca "tủ mất kết nối lúc ra lệnh", đó không phải tủ từ chối.
 */
export function ketQuaLenh(state: string, detail: string | null | undefined): LoiNguoi | null {
  const d = (detail ?? '').trim();
  if (d) {
    const cau = dichTraLoi(d);
    if (cau === null) {
      baoChuaDich(`detail:${d}`);
      return { cau: state === 'FAILED' ? 'Tủ từ chối, lý do chưa có lời dịch' : 'Tủ trả lời một câu chưa có lời dịch' };
    }
    if (state === 'FAILED' && !(d in CAU_MAY_CHU)) return { cau: `Tủ từ chối: ${cau}` };
    return { cau: cau.charAt(0).toUpperCase() + cau.slice(1) };
  }
  switch (state) {
    case 'FAILED': return { cau: 'Tủ từ chối, không nói lý do' };
    case 'TIMEOUT': return { cau: CAU_MAY_CHU['Không thấy StateOnChange xác nhận'] };
    case 'SENT': return { cau: 'Đang chờ tủ xác nhận' };
    case 'SKIPPED_OFFLINE': return { cau: 'Tủ mất kết nối, lệnh chưa được gửi' };
    default: return null;
  }
}

/* ──────────────────────────────────────────────── 3 · NHẬT KÝ TỦ GỬI LÊN ── */

/** `ExceptionMessage.statusCode` 9001–9005 (`server/src/gateway/canhbao.ts` · `dienGiaiMaLoi`). */
export const MA_SU_KIEN: Record<string, string> = {
  'EXC-9001': phanMucGoc('SCHEDULE_MISMATCH').nhan,   // cùng tên với bảng cảnh báo — một nguồn
  'EXC-9002': 'Bảo vệ đã cắt ngõ ra do quá áp',
  'EXC-9003': 'Bảo vệ đã cắt ngõ ra do thấp áp',
  'EXC-9004': 'Điện áp đã về dải — ngõ ra được đóng lại',
  'EXC-9005': 'Bảo vệ đã cắt ngõ ra do quá dòng',
};

/**
 * Một dòng `DeviceLog` ⇒ câu mặt trước. Phần kỹ thuật (thẻ, `requestId`, `epochMs`, `tz`,
 * chữ tủ kèm) KHÔNG đi qua đây — trang giữ nguyên văn `[tag] message` cho "Chi tiết kỹ thuật".
 */
export function dongNhatKy(tag: string | null | undefined, message: string): LoiNguoi {
  const t = (tag ?? '').trim();

  // `ACK-<tên>` — CommandAck không kèm commandId (đặt giờ). Mẫu câu: `onKappAck()` nhánh (a).
  const ack = t.match(/^ACK-(ACK_[A-Z_]+|result=-?\d+)$/);
  if (ack) {
    const so = message.match(/\(requestId=(\d+)\)/)?.[1];
    const lenh = so ? `lệnh #${so}` : 'một lệnh';
    if (ack[1] === 'ACK_OK') return { cau: `Tủ xác nhận ${lenh}` };
    const ly = MA_ACK[ack[1]];
    if (!ly) return chuaDich(t);
    return { cau: `Tủ từ chối ${lenh} — ${ly}` };
  }

  if (t in MA_SU_KIEN) return { cau: MA_SU_KIEN[t] };

  const la = t.match(/^ALARM-LA-(\d+)$/);
  if (la) return { cau: `Tủ báo một loại cảnh báo máy chủ chưa biết tên (loại ${la[1]})` };

  // Dòng tủ tự gửi (`LOG`/`KAPP_LOG`) — chữ tự do của phần sụn, không có bảng nào để khớp.
  // Có thẻ ⇒ chip đúng thẻ ấy. KHÔNG thẻ ⇒ không có mã nào để in: bịa một tên chip là bịa mã.
  if (t) return chuaDich(t);
  baoChuaDich('(dòng nhật ký không thẻ)');
  return { cau: 'Tủ gửi một dòng nhật ký chưa có lời dịch' };
}

/* ─────────────────────────────────────────────────────────────── 4 · TỔNG ── */

/**
 * `true` khi mã có câu dịch. Nhận: tên loại lệnh (`SET_OUTPUT`), tên ACK (`ACK_OK`), thẻ nhật ký
 * (`EXC-9001`, `ACK-ACK_OK`), hoặc `detail:<chuỗi>`.
 */
export function daDich(ma: string): boolean {
  if (ma.startsWith('detail:')) return dichTraLoi(ma.slice(7)) !== null;
  if (ma in LOAI_LENH || ma in MA_ACK || ma in MA_SU_KIEN) return true;
  if (chuanHoaMa(ma) in BANG_MUC) return true;   // mã cảnh báo (popup, bảng cảnh báo)
  const ack = ma.match(/^ACK-(.+)$/);
  if (ack) return ack[1] in MA_ACK;
  return /^ALARM-LA-\d+$/.test(ma);
}

/* ─────────────────────────────────────────────── 5 · CẢNH BÁO (PopupCanhBao) ── */

/**
 * `phanMuc` của `canhbao.ts`, đi QUA bảng dịch. Mã có trong `BANG_MUC` ⇒ nguyên kết quả (câu có
 * dấu của bảng). Mã KHÔNG có ⇒ bản gốc trả `nhan = mã trần` (vd `"DOOR_AJAR"`) — ở đây đổi thành
 * "Sự kiện <MÃ>" và báo chưa dịch một lần. Mức, `biet`, `ma` giữ nguyên: đổi CHỮ, không đổi PHÂN XỬ.
 */
export function phanMuc(code: string | number | null | undefined, mucDoRieng?: number | null): KetQuaPhanMuc {
  const m = phanMucGoc(code, mucDoRieng);
  if (m.biet) return m;
  baoChuaDich(m.ma ? `canh-bao:${m.ma}` : '(cảnh báo không mã)');
  return { ...m, nhan: m.ma ? `Sự kiện ${m.ma}` : 'Cảnh báo không rõ mã' };
}

/** Che mã thiết bị cho tài khoản không phải Quản trị: `DEV-rVZcRERQ0iMP` ⇒ `DEV-…Q0iMP`. */
export function cheMaThietBi(id: string): string {
  const gach = id.indexOf('-');
  const dau = gach >= 0 ? id.slice(0, gach + 1) : '';
  const than = id.slice(dau.length);
  if (than.length <= 5) return `${dau}…`;
  return `${dau}…${than.slice(-5)}`;
}

/* ─────────────────────────────────── 6 · TÊN VIỆC (GD-17 A1/A2 · Tổng quan "Việc cần xem") ── */

/**
 * MỘT mã ⇒ MỘT câu ngắn (≤ 32 ký tự) — **tên việc**. Chip gom nhóm và cột "Việc gì" gọi CÙNG
 * `tenViec()`, nên hai chỗ không thể trôi khỏi nhau (GD-16 vòng 1: chip "lệch so với" ≠ dòng
 * "khác"). Câu dài của `BANG_MUC` ("… Bấm “Đồng bộ giờ”") vẫn sống ở ⓘ / popup / Chi tiết tủ.
 * `web/scripts/kiem_loi_nguoi.ts` [11] đỏ nếu một mã máy chủ dựng được mà thiếu tên, hoặc tên > 32.
 */
export const TEN_VIEC: Record<string, string> = {
  SCHEDULE_MISMATCH: 'Lịch trên tủ khác máy chủ',
  METER_TIMEOUT: 'Không đọc được công tơ',
  GPS_LOST: 'Mất định vị GPS',
  RTC_INVALID: 'Đồng hồ trong tủ sai giờ',
  OVER_VOLTAGE: 'Điện áp cao vượt ngưỡng',
  UNDER_VOLTAGE: 'Điện áp thấp dưới ngưỡng',
  OVER_CURRENT: 'Dòng điện vượt ngưỡng',
  PHASE_LOSS: 'Mất pha',
  CONTACTOR_FAIL: 'Nghi contactor không đóng',
  SAFETY_CUT: 'Bảo vệ đang cắt ngõ ra',
  NVS_ERROR: 'Tủ đang chạy lịch mặc định',
  DOOR_OPEN: 'Cửa tủ đang mở',
  DEVICE_ANNOUNCE: 'Tủ tự báo lỗi',
  MAT_KET_NOI: 'Mất kết nối với máy chủ',
};

/** Tên việc của một mã cảnh báo. Mã chưa có tên ⇒ nhãn của `phanMuc` ("Sự kiện <MÃ>"), KHÔNG bịa. */
export function tenViec(code: string | number | null | undefined): string {
  const m = phanMuc(code);
  return TEN_VIEC[m.ma] ?? m.nhan;
}

/**
 * Số ngõ ra KHÁC NHAU mà câu gốc `SCHEDULE_MISMATCH` nêu (GD-17 A1). Câu do máy chủ dựng
 * (`diffSchedules()` + `moCanhBao()`): "Lịch trên tủ lệch so với server:\nNgõ ra 1: …\nNgõ ra 1
 * timer 0: …". Đếm cụm `Ngõ ra N` KHÁC NHAU trên cả câu (regex toàn cục, không phụ thuộc xuống
 * dòng) — "Ngõ ra 1" hai lần vẫn là 1 ngõ. Không có cụm nào ⇒ `null` (bỏ ngoặc, KHÔNG in "0").
 */
export function soNgoRaLech(message: string | null | undefined): number | null {
  const so = new Set<string>();
  for (const m of (message ?? '').matchAll(/Ng[õo]\s*ra\s+(\d+)/gi)) so.add(m[1]);
  return so.size > 0 ? so.size : null;
}

/* ─────────────────────────────────── 7 · HOẠT ĐỘNG GẦN ĐÂY (GD-17 B8 · nhật ký thao tác) ── */

/**
 * Mã hành động nhật ký (`audit(req, '<MÃ>')` của máy chủ) ⇒ câu người dùng. Kèm LOẠI để tô chấm:
 * `xam` = vào/ra, xem · `xanh` = tác động lên tủ/lịch · `vang` = đổi quyền, xoá, ghi nhận cảnh báo.
 * `kiem_loi_nguoi.ts` [11] quét MỌI mã trong `server/src` — thiếu câu cho mã nào thì đỏ đúng mã ấy.
 */
export const HANH_DONG: Record<string, { cau: string; loai: 'xam' | 'xanh' | 'vang' }> = {
  LOGIN: { cau: 'Đăng nhập hệ thống', loai: 'xam' },
  LOGIN_BI_CHAN: { cau: 'Đăng nhập bị chặn', loai: 'vang' },
  LOGOUT: { cau: 'Đăng xuất', loai: 'xam' },
  CHANGE_PASSWORD: { cau: 'Đổi mật khẩu', loai: 'xam' },
  GIA_HAN: { cau: 'Kéo dài hạn dùng tài khoản', loai: 'vang' },
  CONTROL_OUTPUT: { cau: 'Bật/tắt ngõ ra', loai: 'xanh' },
  CONTROL_ROUTE: { cau: 'Điều khiển cả tuyến', loai: 'xanh' },
  RELEASE_CONTROL: { cau: 'Trả tủ về chạy theo lịch', loai: 'xanh' },
  SYNC_RTC: { cau: 'Đồng bộ đồng hồ tủ', loai: 'xanh' },
  LENH_DON: { cau: 'Gửi lệnh tới tủ', loai: 'xanh' },
  LENH_DAT_GIO: { cau: 'Đặt giờ cho tủ', loai: 'xanh' },
  LENH_DOC_CAU_HINH: { cau: 'Đọc cấu hình tủ', loai: 'xam' },
  LENH_DAT_CAU_HINH: { cau: 'Ghi cấu hình tủ', loai: 'xanh' },
  DOC_LICH_TU_TU: { cau: 'Đọc lịch từ tủ', loai: 'xam' },
  DO_LICH_THO: { cau: 'Gửi lịch thô để đo', loai: 'vang' },
  DO_MANG_THO: { cau: 'Gửi gói mạng thô để đo', loai: 'vang' },
  MO_PHONG_NGO_VAO: { cau: 'Bật/tắt mô phỏng ngõ vào', loai: 'vang' },
  SAVE_SCHEDULE_DRAFT: { cau: 'Lưu nháp lịch', loai: 'xam' },
  APPLY_SCHEDULE: { cau: 'Đẩy lịch xuống tủ', loai: 'xanh' },
  ROLLBACK_SCHEDULE: { cau: 'Lùi về bản lịch cũ', loai: 'xanh' },
  CREATE_STRATEGY: { cau: 'Tạo chiến lược', loai: 'xam' },
  UPDATE_STRATEGY: { cau: 'Sửa chiến lược', loai: 'xam' },
  DELETE_STRATEGY: { cau: 'Xoá chiến lược', loai: 'vang' },
  APPLY_STRATEGY: { cau: 'Áp chiến lược', loai: 'xanh' },
  CREATE_PROJECT: { cau: 'Tạo dự án', loai: 'xam' },
  PROJECTID_BI_BO_QUA: { cau: 'Bỏ qua dự án khi tạo tài khoản', loai: 'vang' },
  CREATE_ROUTE: { cau: 'Tạo tuyến chiếu sáng', loai: 'xam' },
  UPDATE_ROUTE: { cau: 'Sửa tuyến chiếu sáng', loai: 'xam' },
  DELETE_ROUTE: { cau: 'Xoá tuyến chiếu sáng', loai: 'vang' },
  CREATE_CABINET: { cau: 'Thêm tủ mới', loai: 'xam' },
  UPDATE_CABINET: { cau: 'Sửa thông tin tủ', loai: 'xam' },
  UNASSIGN_CABINET: { cau: 'Gỡ tủ khỏi tuyến', loai: 'vang' },
  REATTACH_CABINET: { cau: 'Gắn lại tủ vào tuyến', loai: 'xam' },
  UPDATE_OUTPUT: { cau: 'Sửa cấu hình ngõ ra', loai: 'xam' },
  UPDATE_USER: { cau: 'Sửa tài khoản', loai: 'vang' },
  DELETE_USER: { cau: 'Xoá tài khoản', loai: 'vang' },
  SET_ROUTE_ACCESS: { cau: 'Phân quyền theo tuyến', loai: 'vang' },
  QUYEN_BI_BOT_THEO_NGUOI_TAO: { cau: 'Quyền bị bớt theo người tạo', loai: 'vang' },
  TUYEN_BI_BOT_THEO_NGUOI_TAO: { cau: 'Tuyến bị bớt theo người tạo', loai: 'vang' },
  ACK_ALARM: { cau: 'Ghi nhận cảnh báo', loai: 'vang' },
  XU_LY_CANH_BAO: { cau: 'Đánh dấu đã xử lý', loai: 'xanh' },
  LAY_LICH_TU_TU: { cau: 'Lấy lịch trong tủ làm bản máy chủ', loai: 'xanh' },
  TU_BI_TU_CHOI: { cau: 'Xin vào bị từ chối', loai: 'vang' },
};

/**
 * TK-25 — HOẠT ĐỘNG GẦN ĐÂY CHỈ HIỆN SỰ KIỆN CÓ NGHĨA. Lee 10/10: *"tài khoản login, vận hành ghi nhận lỗi, tủ
 *   login bị từ chối; không hiện thông báo vô tác dụng như đọc cấu hình tủ"*. Danh sách TRẮNG, không phải đen:
 *   một mã mới máy chủ ghi ra sẽ KHÔNG tự lọt vào ô này cho tới khi có người quyết nó đáng xem — ô này là thứ
 *   người trực đọc mỗi sáng, không phải nhật ký đầy đủ (đã có /nhat-ky).
 */
export const HOAT_DONG_CO_NGHIA: readonly string[] = [
  'LOGIN', 'LOGIN_BI_CHAN', 'TU_BI_TU_CHOI',
  'ACK_ALARM', 'XU_LY_CANH_BAO',
  'CONTROL_OUTPUT', 'CONTROL_ROUTE', 'RELEASE_CONTROL',
  'APPLY_SCHEDULE', 'ROLLBACK_SCHEDULE', 'LAY_LICH_TU_TU', 'APPLY_STRATEGY', 'SYNC_RTC',
  'CREATE_CABINET', 'UNASSIGN_CABINET', 'DELETE_ROUTE', 'DELETE_USER', 'SET_ROUTE_ACCESS',
];

/**
 * Một hàng nhật ký ⇒ câu ngắn cho ô "Hoạt động gần đây". `tenTu` = tên tủ trang đã tra từ
 * `target` (`cabinet:<id>`); `null` ⇒ không kèm. `CONTROL_OUTPUT` nêu ngõ và BẬT/TẮT khi `detail`
 * đúng hình dạng `{ outputs: [{ index, on }] }` của `api/control.ts`.
 * ⛔ Mã chưa có câu ⇒ "Thao tác chưa có tên" + báo chưa dịch MỘT lần — ô này không có chỗ cho
 *   chip mã (mặt trước, mọi vai); mã nằm ở trang Nhật ký thao tác.
 */
export function dongHoatDong(action: string, detail: unknown, tenTu: string | null): { cau: string; loai: 'xam' | 'xanh' | 'vang' } {
  const g = HANH_DONG[action];
  if (!g) { baoChuaDich(`nhat-ky:${action}`); return { cau: 'Thao tác chưa có tên', loai: 'xam' }; }
  let cau = g.cau;
  if (action === 'CONTROL_OUTPUT') {
    const ds = (detail as { outputs?: unknown } | null)?.outputs;
    if (Array.isArray(ds) && ds.length > 0 && ds.every((o) => ngoHopLe((o as Tai).index) && typeof (o as Tai).on === 'boolean')) {
      const bat = ds.filter((o) => (o as Tai).on).map((o) => (o as Tai).index as number);
      const tat = ds.filter((o) => !(o as Tai).on).map((o) => (o as Tai).index as number);
      cau = [bat.length ? `Bật ngõ ${bat.join(', ')}` : '', tat.length ? `Tắt ngõ ${tat.join(', ')}` : ''].filter(Boolean).join(' · ');
    }
  }
  /* TK-25: thiết bị bị từ chối — nêu LÝ DO máy chủ ghi (`detail.lyDo`), vì "bị từ chối" suông thì người trực không biết đi đâu. */
  if (action === 'TU_BI_TU_CHOI') {
    const lyDo = (detail as { lyDo?: unknown } | null)?.lyDo;
    if (typeof lyDo === 'string' && lyDo.trim()) cau = `${cau} — ${lyDo.trim()}`;
  }
  /* TK-25: ghi nhận / đã xử lý — nêu TÊN VIỆC từ `detail.code` để dòng đọc được là việc gì, không chỉ "cảnh báo". */
  if (action === 'ACK_ALARM' || action === 'XU_LY_CANH_BAO') {
    const code = (detail as { code?: unknown } | null)?.code;
    if (typeof code === 'string' && code) cau = `${cau} · ${tenViec(code)}`;
  }
  return { cau: tenTu ? `${cau} · ${tenTu}` : cau, loai: g.loai };
}
